import React, { useEffect, useState, useRef } from 'react';
import { useHospitalSelection } from '../../hooks/useHospitalSelection';
import { usePersistedState } from '../../hooks/usePersistedState';
import LineChart from '../../components/Charts/LineChart';
import CustomSelect from '../../components/Common/CustomSelect';
import LoadingNotice from '../../components/LoadingNotice/LoadingNotice';
import { TmbPillSelect } from '../../components/TMBControls';
import { generatePDFIds } from '../../utils/pdfIdHelper';
import { findColumnIndex, COLUMN_CANDIDATES } from '../../utils/dataUtils';
import { extractQuestionsFromRows, QuestionInfo } from '../../utils/coreUtils';
import { useClickOutside } from '../../hooks/useClickOutside';
import '../../styles/dropdown.css';
import './PremAgreement.css';
import html2canvas from "html2canvas"

interface TooltipPointMeta {
    numerator: number;
    denominator: number;
}

interface TooltipMetaMap {
    [period: string]: {
        [group: string]: TooltipPointMeta | undefined;
    };
}

interface ChartSeries {
    name: string;
    data: (number | null)[];
}

interface ChartDataWithTooltip {
    categories: string[];
    series: ChartSeries[];
    tooltipMeta: TooltipMetaMap;
}

const HIGHLIGHTED_QUESTION_KEYS = new Set(['rsp3', 'rsp4', 'rsp_nurses', 'rsp_obprovider']);

const BASE_MAX_RACE_SELECTIONS = 6;
const DEFAULT_RACE_SELECTIONS = ['Hispanic', 'Black/African American', 'Asian', 'Race Not Documented', 'NH White', 'Other Race/Ethnicity'];

// Maps raw API race_ethnicity value → display label (for multi-type rows)
const RACE_API_TO_DISPLAY: Record<string, string> = {
    'Black': 'Black/African American',
    'Not reported': 'Race Not Documented',
    'Other': 'Other Race/Ethnicity',
};

// Maps display label → { apiValue, raceType } for filtering rows
const RACE_DISPLAY_TO_API: Record<string, { apiValue: string; raceType: string }> = {
    'BIPOC':                   { apiValue: 'All Other Groups', raceType: 'binary' },
    'Hispanic':                { apiValue: 'Hispanic',         raceType: 'multi'  },
    'Black/African American':  { apiValue: 'Black',            raceType: 'multi'  },
    'Asian':                   { apiValue: 'Asian',            raceType: 'multi'  },
    'Race Not Documented':     { apiValue: 'Not reported',     raceType: 'multi'  },
    'NH White':                { apiValue: 'NH White',         raceType: 'multi'  },
    'Other Race/Ethnicity':    { apiValue: 'Other',            raceType: 'multi'  },
};

const RACE_COLORS: Record<string, string> = {
    'BIPOC':                  '#F2E500',
    'Black/African American': '#0072B2',
    'NH White':               '#D55E00',
    'Hispanic':               '#F0E441',
    'Asian':                  '#009E73',
    'Race Not Documented':    '#cc79a7',
    'Other Race/Ethnicity':   '#56B4E9',
};

const INSURANCE_CHART_COLORS = ['#F2E500', '#78DFFE'];

const PremAgreement: React.FC = () => {
    const { selectedHospitalIds, isMultiSelect, refreshKey } = useHospitalSelection();
    const [raceChartData, setRaceChartData] = useState<ChartDataWithTooltip>({ categories: [], series: [], tooltipMeta: {} });
    const [insuranceChartData, setInsuranceChartData] = useState<any>({ categories: [], series: [] });
    const [loading, setLoading] = useState(false);
    const [insuranceLoading, setInsuranceLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(true);
    const [timePeriod, setTimePeriod] = usePersistedState<string>('prem_agreement_timePeriod', 'monthly');
    const [selectedQuestion, setSelectedQuestion] = usePersistedState<string>('prem_agreement_question', '');
    const [questions, setQuestions] = useState<QuestionInfo[]>([]);
    const [rawData, setRawData] = useState<any>(null);
    const [rawInsuranceData, setRawInsuranceData] = useState<any>(null);
    const [questionDropdownOpen, setQuestionDropdownOpen] = useState(false);
    const questionDropdownRef = useRef<HTMLDivElement>(null);
    const premContainerRef = useRef<HTMLDivElement>(null)
    const premContainerRef2 = useRef<HTMLDivElement>(null)

    const [selectedRaceGroups, setSelectedRaceGroups] = usePersistedState<string[]>('prem_agreement_race_groups', DEFAULT_RACE_SELECTIONS);
    const [availableRaceGroups, setAvailableRaceGroups] = useState<string[]>([]);
    const [raceDropdownOpen, setRaceDropdownOpen] = useState(false);

    useClickOutside([questionDropdownRef], () => setQuestionDropdownOpen(false), questionDropdownOpen);

    useEffect(() => {
        if (selectedHospitalIds.length === 0) {
            setRaceChartData({ categories: [], series: [], tooltipMeta: {} });
            setRawData(null);
            setInitialLoading(false);
            return;
        }

        setLoading(true);
        const hospitalIdsParam = selectedHospitalIds.join(',');

        fetch(`/api/prem_agreement_race/${hospitalIdsParam}`)
            .then(res => res.json())
            .then(data => {
                setRawData(data);
                setLoading(false);
                setInitialLoading(false);
            })
            .catch(err => {
                console.error('Error fetching PREM agreement race data:', err);
                setRawData(null);
                setLoading(false);
                setInitialLoading(false);
            });
    }, [selectedHospitalIds, refreshKey]);

    useEffect(() => {
        if (selectedHospitalIds.length === 0) {
            setInsuranceChartData({ categories: [], series: [] });
            setRawInsuranceData(null);
            return;
        }

        setInsuranceLoading(true);
        const hospitalIdsParam = selectedHospitalIds.join(',');

        fetch(`/api/prem_agreement_insurance/${hospitalIdsParam}`)
            .then(res => res.json())
            .then(data => {
                setRawInsuranceData(data);
                setInsuranceLoading(false);
            })
            .catch(err => {
                console.error('Error fetching PREM agreement insurance data:', err);
                setRawInsuranceData(null);
                setInsuranceLoading(false);
            });
    }, [selectedHospitalIds, refreshKey]);

    useEffect(() => {
        if (!rawData) {
            setQuestions([]);
            return;
        }

        const rows = rawData?.response?.result?.rows || [];
        const columns = rawData?.response?.result?.column_order || [];

        const questionNameIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionName);
        const questionLabelIdx = findColumnIndex(columns, COLUMN_CANDIDATES.questionLabel);

        if (questionNameIdx === -1 || rows.length === 0) {
            setQuestions([]);
            return;
        }

        const extracted = extractQuestionsFromRows(rows, questionNameIdx, questionLabelIdx);
        setQuestions(extracted);

        if (extracted.length > 0 && (!selectedQuestion || !extracted.some(q => q.name === selectedQuestion))) {
            setSelectedQuestion(extracted[0].name);
        }

        // Extract available race/ethnicity groups from data
        const raceColIdx = findColumnIndex(columns, COLUMN_CANDIDATES.race);
        const raceTypeColIdx = findColumnIndex(columns, ['race_ethnicity_type', 'raceethnicitytype', 'race_type', 'racetype']);
        if (raceColIdx !== -1) {
            const groupSet = new Set<string>();
            rows.forEach((r: any) => {
                const raceVal = String(r[raceColIdx] || '').trim();
                const raceType = raceTypeColIdx !== -1 ? String(r[raceTypeColIdx] || '').trim().toLowerCase() : '';
                if (raceType === 'binary') {
                    if (raceVal === 'All Other Groups') groupSet.add('BIPOC');
                } else {
                    const display = RACE_API_TO_DISPLAY[raceVal] || raceVal;
                    if (display) groupSet.add(display);
                }
            });
            const preferredOrder = ['BIPOC', 'Hispanic', 'Black/African American', 'Asian', 'Race Not Documented', 'Other Race/Ethnicity', 'NH White'];
            const available = preferredOrder.filter(r => groupSet.has(r));
            const remaining = Array.from(groupSet).filter(r => !preferredOrder.includes(r)).sort();
            setAvailableRaceGroups([...available, ...remaining]);
        }
    }, [rawData]);

    useEffect(() => {
        if (!rawData || !selectedQuestion) {
            setRaceChartData({ categories: [], series: [], tooltipMeta: {} });
            return;
        }

        if (isMultiSelect) {
            processMultiSelectData(rawData, selectedQuestion, timePeriod, selectedRaceGroups);
        } else {
            processSingleSelectData(rawData, selectedQuestion, timePeriod, selectedRaceGroups);
        }
    }, [rawData, selectedQuestion, timePeriod, isMultiSelect, selectedRaceGroups]);

    useEffect(() => {
        if (!rawInsuranceData || !selectedQuestion) {
            setInsuranceChartData({ categories: [], series: [] });
            return;
        }

        if (isMultiSelect) {
            processInsuranceMultiSelectData(rawInsuranceData, selectedQuestion, timePeriod);
        } else {
            processInsuranceSingleSelectData(rawInsuranceData, selectedQuestion, timePeriod);
        }
    }, [rawInsuranceData, selectedQuestion, timePeriod, isMultiSelect]);

    const monthToNumber: Record<string, number> = {
        Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
        Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
    };

    const sortPeriods = (periods: string[], period: string): string[] => {
        return [...periods].sort((a, b) => {
            if (period === 'monthly') {
                const [monthA, yearA] = a.split(' ');
                const [monthB, yearB] = b.split(' ');
                const yDiff = (parseInt(yearA, 10) || 0) - (parseInt(yearB, 10) || 0);
                if (yDiff !== 0) return yDiff;
                return (monthToNumber[monthA] || 0) - (monthToNumber[monthB] || 0);
            } else if (period === 'quarterly') {
                const yearA = parseInt(a.replace(/\D/g, '').slice(-4), 10) || 0;
                const yearB = parseInt(b.replace(/\D/g, '').slice(-4), 10) || 0;
                if (yearA !== yearB) return yearA - yearB;
                const qA = parseInt(a.match(/Q(\d)/)?.[1] || '0');
                const qB = parseInt(b.match(/Q(\d)/)?.[1] || '0');
                return qA - qB;
            } else if (period === 'bi-yearly') {
                const partsA = a.split(' ');
                const partsB = b.split(' ');
                const yearA = parseInt(partsA[partsA.length - 1], 10) || 0;
                const yearB = parseInt(partsB[partsB.length - 1], 10) || 0;
                if (yearA !== yearB) return yearA - yearB;
                const isFirstHalfA = partsA[0] === 'Jan-Jun';
                const isFirstHalfB = partsB[0] === 'Jan-Jun';
                return isFirstHalfA === isFirstHalfB ? 0 : (isFirstHalfA ? -1 : 1);
            } else if (period === 'yearly') {
                return (parseInt(a, 10) || 0) - (parseInt(b, 10) || 0);
            }
            return 0;
        });
    };

    const isAllOthersValue = (v: string) => v === 'BIPOC';
    const isNHWhiteValue = (v: string) => v === 'NH White';

    const handleRaceToggle = (race: string, checked: boolean) => {
        const isBIPOC = isAllOthersValue(race);
        const isNHWhite = isNHWhiteValue(race);
        const allOthersActive = selectedRaceGroups.some(isAllOthersValue);

        if (checked) {
            if (isBIPOC) {
                const nhSelected = selectedRaceGroups.some(isNHWhiteValue);
                setSelectedRaceGroups(nhSelected ? ['BIPOC', 'NH White'] : ['BIPOC']);
                return;
            }
            if (allOthersActive && !isNHWhite) return;
            if (selectedRaceGroups.length >= BASE_MAX_RACE_SELECTIONS) return;
            if (!selectedRaceGroups.includes(race)) {
                setSelectedRaceGroups([...selectedRaceGroups, race]);
            }
        } else {
            if (isBIPOC) {
                setSelectedRaceGroups([...DEFAULT_RACE_SELECTIONS]);
                return;
            }
            setSelectedRaceGroups(selectedRaceGroups.filter(r => r !== race));
        }
    };

    const getColumnIndices = (columns: string[]) => ({
        questionColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.questionName),
        periodColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.month),
        intervalColIdx: findColumnIndex(columns, ['intervalName', 'intervalname', 'interval_name', 'Interval_Name', 'interval']),
        raceColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.race),
        raceTypeColIdx: findColumnIndex(columns, ['race_ethnicity_type', 'raceethnicitytype', 'race_type', 'racetype']),
        numeratorColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.numerator),
        denominatorColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.denominator),
    });

    const getInsuranceColumnIndices = (columns: string[]) => ({
        questionColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.questionName),
        periodColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.month),
        intervalColIdx: findColumnIndex(columns, ['intervalName', 'intervalname', 'interval_name', 'Interval_Name', 'interval']),
        insuranceColIdx: findColumnIndex(columns, ['insurance_category', 'insurance_type', 'insurancetype', 'insurance', 'Insurance_Type', 'InsuranceType']),
        percentColIdx: findColumnIndex(columns, ['percent_strong', 'percent', 'percentage', 'pct', 'value', 'percent_agree']),
        numeratorColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.numerator),
        denominatorColIdx: findColumnIndex(columns, COLUMN_CANDIDATES.denominator),
    });

    const filterRowsByInterval = (rows: any[], period: string, intervalColIdx: number): any[] => {
        const normalizeInterval = (v: any) => String(v ?? '').trim().toLowerCase();
        return rows.filter((row: any) => {
            const intervalValue = normalizeInterval(row[intervalColIdx]);
            switch (period) {
                case 'monthly': return intervalValue === 'monthly';
                case 'quarterly': return intervalValue === 'quarterly';
                case 'bi-yearly': return intervalValue === 'bi-yearly' || intervalValue === 'bi-annually';
                case 'yearly': return intervalValue === 'yearly';
                default: return intervalValue === 'monthly';
            }
        });
    };

    const filterFromSep2025 = (rows: any[], periodColIdx: number, period: string): any[] => {
        return rows.filter((row: any) => {
            const periodStr = String(row[periodColIdx] || '');
            if (period === 'monthly') {
                const [month, yearStr] = periodStr.split(' ');
                const y = parseInt(yearStr, 10) || 0;
                const m = monthToNumber[month];
                return y > 2025 || (y === 2025 && m >= 9);
            }
            if (period === 'quarterly') {
                const year = parseInt(periodStr.replace(/\D/g, '').slice(-4), 10) || 0;
                const q = parseInt(periodStr.match(/Q(\d)/)?.[1] || '0');
                return year > 2025 || (year === 2025 && q >= 3);
            }
            if (period === 'bi-yearly') {
                const parts = periodStr.split(' ');
                const year = parseInt(parts[parts.length - 1], 10) || 0;
                const half = parts[0];
                return year > 2025 || (year === 2025 && half === 'Jul-Dec');
            }
            if (period === 'yearly') {
                const year = parseInt(periodStr, 10) || 0;
                return year >= 2025;
            }
            return true;
        });
    };

    const buildChartFromRowsAggregated = (
        filtered: any[], periodColIdx: number, groupColIdx: number,
        numeratorColIdx: number, denominatorColIdx: number,
        period: string, groupNames: string[]
    ): ChartDataWithTooltip => {
        const periodMap: Record<string, Record<string, { numSum: number; denSum: number }>> = {};
        filtered.forEach((row: any) => {
            const p = String(row[periodColIdx] || '');
            const group = String(row[groupColIdx] || '').trim();
            const num = parseFloat(row[numeratorColIdx]);
            const den = parseFloat(row[denominatorColIdx]);

            if (!p || !groupNames.includes(group)) return;
            if (isNaN(num) || isNaN(den)) return;
            if (!periodMap[p]) periodMap[p] = {};
            if (!periodMap[p][group]) periodMap[p][group] = { numSum: 0, denSum: 0 };
            periodMap[p][group].numSum += num;
            periodMap[p][group].denSum += den;
        });

        const sortedPeriods = sortPeriods(Object.keys(periodMap), period);
        const tooltipMeta: TooltipMetaMap = {};

        sortedPeriods.forEach((periodLabel) => {
            tooltipMeta[periodLabel] = {};
            groupNames.forEach((groupName) => {
                const entry = periodMap[periodLabel]?.[groupName];
                if (!entry || entry.denSum === 0) return;
                tooltipMeta[periodLabel][groupName] = {
                    numerator: entry.numSum,
                    denominator: entry.denSum,
                };
            });
        });

        return {
            categories: sortedPeriods,
            series: groupNames.map(name => ({
                name,
                data: sortedPeriods.map(p => {
                    const entry = periodMap[p]?.[name];
                    if (!entry || entry.denSum === 0) return null;
                    return Math.round((entry.numSum / entry.denSum) * 100 * 100) / 100;
                })
            })),
            tooltipMeta
        };
    };

    const buildRaceChartWithGroups = (
        filtered: any[], periodColIdx: number, raceColIdx: number,
        raceTypeColIdx: number, numeratorColIdx: number, denominatorColIdx: number,
        period: string, groups: string[]
    ): ChartDataWithTooltip => {
        const periodMap: Record<string, Record<string, { numSum: number; denSum: number }>> = {};

        filtered.forEach((row: any) => {
            const p = String(row[periodColIdx] || '');
            const raceVal = String(row[raceColIdx] || '').trim();
            const raceType = raceTypeColIdx !== -1 ? String(row[raceTypeColIdx] || '').trim().toLowerCase() : '';
            const num = parseFloat(row[numeratorColIdx]);
            const den = parseFloat(row[denominatorColIdx]);

            if (!p || isNaN(num) || isNaN(den)) return;

            let displayLabel: string | null = null;

            // BIPOC maps to binary "All Other Groups"
            if (raceType === 'binary' && raceVal === 'All Other Groups' && groups.includes('BIPOC')) {
                displayLabel = 'BIPOC';
            } else if (raceType !== 'binary') {
                // multi rows: map API value → display label
                const mapped = RACE_API_TO_DISPLAY[raceVal] || raceVal;
                if (groups.includes(mapped)) displayLabel = mapped;
            }

            if (!displayLabel) return;

            if (!periodMap[p]) periodMap[p] = {};
            if (!periodMap[p][displayLabel]) periodMap[p][displayLabel] = { numSum: 0, denSum: 0 };
            periodMap[p][displayLabel].numSum += num;
            periodMap[p][displayLabel].denSum += den;
        });

        const sortedPeriods = sortPeriods(Object.keys(periodMap), period);
        const tooltipMeta: TooltipMetaMap = {};

        sortedPeriods.forEach((periodLabel) => {
            tooltipMeta[periodLabel] = {};
            groups.forEach((group) => {
                const entry = periodMap[periodLabel]?.[group];
                if (!entry || entry.denSum === 0) return;
                tooltipMeta[periodLabel][group] = {
                    numerator: entry.numSum,
                    denominator: entry.denSum,
                };
            });
        });

        return {
            categories: sortedPeriods,
            series: groups.map(name => ({
                name,
                data: sortedPeriods.map(p => {
                    const entry = periodMap[p]?.[name];
                    if (!entry || entry.denSum === 0) return null;
                    return Math.round((entry.numSum / entry.denSum) * 100 * 100) / 100;
                })
            })),
            tooltipMeta
        };
    };

    const processSingleSelectData = (data: any, question: string, period: string, raceGroups: string[]) => {
        const rows = data?.response?.result?.rows || [];
        const columns = data?.response?.result?.column_order || [];
        const { questionColIdx, periodColIdx, intervalColIdx, raceColIdx, raceTypeColIdx, numeratorColIdx, denominatorColIdx } = getColumnIndices(columns);

        if (questionColIdx === -1 || periodColIdx === -1 || raceColIdx === -1 || numeratorColIdx === -1 || denominatorColIdx === -1) {
            setRaceChartData({ categories: [], series: [], tooltipMeta: {} });
            return;
        }

        let filtered = rows.filter((r: any) => r[questionColIdx] === question);

        if (intervalColIdx !== -1) {
            filtered = filterRowsByInterval(filtered, period, intervalColIdx);
        }

        filtered = filterFromSep2025(filtered, periodColIdx, period);

        setRaceChartData(buildRaceChartWithGroups(filtered, periodColIdx, raceColIdx, raceTypeColIdx, numeratorColIdx, denominatorColIdx, period, raceGroups));
    };

    const processMultiSelectData = (data: any, question: string, period: string, raceGroups: string[]) => {
        const rows = data?.response?.result?.rows || [];
        const columns = data?.response?.result?.column_order || [];
        const { questionColIdx, periodColIdx, intervalColIdx, raceColIdx, raceTypeColIdx, numeratorColIdx, denominatorColIdx } = getColumnIndices(columns);

        if (questionColIdx === -1 || periodColIdx === -1 || raceColIdx === -1 || numeratorColIdx === -1 || denominatorColIdx === -1) {
            setRaceChartData({ categories: [], series: [], tooltipMeta: {} });
            return;
        }

        let filtered = rows.filter((r: any) => r[questionColIdx] === question);

        if (intervalColIdx !== -1) {
            filtered = filterRowsByInterval(filtered, period, intervalColIdx);
        }

        filtered = filterFromSep2025(filtered, periodColIdx, period);

        setRaceChartData(buildRaceChartWithGroups(filtered, periodColIdx, raceColIdx, raceTypeColIdx, numeratorColIdx, denominatorColIdx, period, raceGroups));
    };

    const processInsuranceSingleSelectData = (data: any, question: string, period: string) => {
        const rows = data?.response?.result?.rows || [];
        const columns = data?.response?.result?.column_order || [];
        const { questionColIdx, periodColIdx, intervalColIdx, insuranceColIdx, numeratorColIdx, denominatorColIdx } = getInsuranceColumnIndices(columns);

        if (questionColIdx === -1 || periodColIdx === -1 || insuranceColIdx === -1 || numeratorColIdx === -1 || denominatorColIdx === -1) {
            setInsuranceChartData({ categories: [], series: [] });
            return;
        }

        let filtered = rows.filter((r: any) => r[questionColIdx] === question);

        if (intervalColIdx !== -1) {
            filtered = filterRowsByInterval(filtered, period, intervalColIdx);
        }

        filtered = filterFromSep2025(filtered, periodColIdx, period);

        const allTypes = new Set(filtered.map((r: any) => String(r[insuranceColIdx] || '').trim()));
        const insuranceTypes = ['Public Insurance', 'Private Insurance'].filter(t => allTypes.has(t));

        setInsuranceChartData(buildChartFromRowsAggregated(filtered, periodColIdx, insuranceColIdx, numeratorColIdx, denominatorColIdx, period, insuranceTypes));
    };

    const processInsuranceMultiSelectData = (data: any, question: string, period: string) => {
        const rows = data?.response?.result?.rows || [];
        const columns = data?.response?.result?.column_order || [];
        const { questionColIdx, periodColIdx, intervalColIdx, insuranceColIdx, numeratorColIdx, denominatorColIdx } = getInsuranceColumnIndices(columns);

        if (questionColIdx === -1 || periodColIdx === -1 || insuranceColIdx === -1 || numeratorColIdx === -1 || denominatorColIdx === -1) {
            setInsuranceChartData({ categories: [], series: [] });
            return;
        }

        let filtered = rows.filter((r: any) => r[questionColIdx] === question);

        if (intervalColIdx !== -1) {
            filtered = filterRowsByInterval(filtered, period, intervalColIdx);
        }

        filtered = filterFromSep2025(filtered, periodColIdx, period);

        const allTypes = new Set(filtered.map((r: any) => String(r[insuranceColIdx] || '').trim()));
        const insuranceTypes = ['Public Insurance', 'Private Insurance'].filter(t => allTypes.has(t));

        setInsuranceChartData(buildChartFromRowsAggregated(filtered, periodColIdx, insuranceColIdx, numeratorColIdx, denominatorColIdx, period, insuranceTypes));
    };

    const raceChartRef = React.useRef<any>(null);
    const insuranceChartRef = React.useRef<any>(null);

    if (initialLoading) {
        return <LoadingNotice title="Loading PREM agreement data..." />;
    }

    const closeMenu = (menuId: string) => {
        const menu = document.getElementById(menuId);
        if (menu) menu.style.display = 'none';
    };

    const selectedQuestionData = questions.find(q => q.name === selectedQuestion);
    const selectedQuestionLabel = selectedQuestionData
        ? selectedQuestionData.fullText
        : '';
    const isHighlightedQuestion = (question: QuestionInfo): boolean => {
        const normalizedCode = String(question.code || '').trim().toLowerCase();
        const normalizedName = String(question.name || '').trim().toLowerCase();
        return HIGHLIGHTED_QUESTION_KEYS.has(normalizedCode) || HIGHLIGHTED_QUESTION_KEYS.has(normalizedName);
    };
    const bipocActive = selectedRaceGroups.some(isAllOthersValue);
    const raceChartColors = selectedRaceGroups.map(g => {
        if (bipocActive && isAllOthersValue(g)) return '#F2E500';
        if (bipocActive && isNHWhiteValue(g))   return '#78DFFE';
        return RACE_COLORS[g] || '#888888';
    });

    const escapeHtml = (value: string): string => value
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');

    const formatTooltipPercent = (value: number | null): string => {
        if (value === null || value === undefined || Number.isNaN(value)) return 'No Data';
        return `${Math.round(value)}%`;
    };

    const renderRaceTooltip = ({ category, dataPointIndex }: { category: string; dataPointIndex: number }): string => {
        const rows = raceChartData.series.map((seriesItem, index) => {
            const value = seriesItem.data[dataPointIndex];
            const completedSurveys = raceChartData.tooltipMeta?.[category]?.[seriesItem.name]?.denominator;

            return `
                <div style="display:flex; flex-direction:column; gap:2px; margin-top:${index === 0 ? '0' : '8px'};">
                    <div style="display:flex; align-items:center; gap:8px;">
                        <span style="width:10px; height:10px; border-radius:999px; background:${raceChartColors[index % raceChartColors.length]}; display:inline-block;"></span>
                        <span>${escapeHtml(seriesItem.name)}:</span>
                        <span style="font-weight:600;">${formatTooltipPercent(value)}</span>
                    </div>
                    <div style="padding-left:18px; color:#555;">Surveys completed: <span style="font-weight:600;">${completedSurveys ?? 'No Data'}</span></div>
                </div>
            `;
        }).join('');

        return `
            <div style="font-family:Helvetica, Arial, sans-serif; font-size:12px;">
                <div style="background:#eceff1; color:#263238; padding:6px 10px; border-bottom:1px solid #d9dee3;">${escapeHtml(category)}</div>
                <div style="padding:8px 10px;">
                    ${rows}
                </div>
            </div>
        `;
    };

    const renderInsuranceTooltip = ({ category, dataPointIndex }: { category: string; dataPointIndex: number }): string => {
        const rows = insuranceChartData.series.map((seriesItem: ChartSeries, index: number) => {
            const value = seriesItem.data[dataPointIndex];
            const completedSurveys = insuranceChartData.tooltipMeta?.[category]?.[seriesItem.name]?.denominator;

            return `
                <div style="display:flex; flex-direction:column; gap:2px; margin-top:${index === 0 ? '0' : '8px'};">
                    <div style="display:flex; align-items:center; gap:8px;">
                        <span style="width:10px; height:10px; border-radius:999px; background:${INSURANCE_CHART_COLORS[index % INSURANCE_CHART_COLORS.length]}; display:inline-block;"></span>
                        <span>${escapeHtml(seriesItem.name)}:</span>
                        <span style="font-weight:600;">${formatTooltipPercent(value)}</span>
                    </div>
                    <div style="padding-left:18px; color:#555;">Surveys completed: <span style="font-weight:600;">${completedSurveys ?? 'No Data'}</span></div>
                </div>
            `;
        }).join('');

        return `
            <div style="font-family:Helvetica, Arial, sans-serif; font-size:12px;">
                <div style="background:#eceff1; color:#263238; padding:6px 10px; border-bottom:1px solid #d9dee3;">${escapeHtml(category)}</div>
                <div style="padding:8px 10px;">
                    ${rows}
                </div>
            </div>
        `;
    };

    const handleDownloadRaceCSV = () => {
        const hospitalIds = selectedHospitalIds.join(', ');
        const seriesNames = raceChartData.series.map(s => s.name);
        const header = ['Hospital ID(s)', 'Time Period', ...seriesNames.flatMap(n => [`${n} (%)`, `${n} - Surveys Completed`])];
        const dataRows = raceChartData.categories.map((cat, i) => {
            const values = raceChartData.series.flatMap(s => {
                const pct = s.data[i] != null ? `${s.data[i]}%` : '';
                const surveys = raceChartData.tooltipMeta?.[cat]?.[s.name]?.denominator ?? '';
                return [pct, surveys];
            });
            return [`"${hospitalIds}"`, cat, ...values];
        });
        const csvContent = [header, ...dataRows].map(r => r.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'PREM Agreement by Race Ethnicity.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleDownloadInsuranceCSV = () => {
        const hospitalIds = selectedHospitalIds.join(', ');
        const seriesNames = insuranceChartData.series.map((s: ChartSeries) => s.name);
        const header = ['Hospital ID(s)', 'Time Period', ...seriesNames.flatMap((n: string) => [`${n} (%)`, `${n} - Surveys Completed`])];
        const dataRows = insuranceChartData.categories.map((cat: string, i: number) => {
            const values = insuranceChartData.series.flatMap((s: ChartSeries) => {
                const pct = s.data[i] != null ? `${s.data[i]}%` : '';
                const surveys = insuranceChartData.tooltipMeta?.[cat]?.[s.name]?.denominator ?? '';
                return [pct, surveys];
            });
            return [`"${hospitalIds}"`, cat, ...values];
        });
        const csvContent = [header, ...dataRows].map((r: any[]) => r.join(',')).join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'PREM Agreement by Insurance Type.csv';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };
    const centerTitlesOnClone = (clonedDoc: Document) => {
        clonedDoc.querySelectorAll<HTMLElement>('.chart-title, .chart-subtitle').forEach(el => {
            el.style.textAlign = 'center';
        });
    };

    const handleDownloadPNG = async (ref: React.RefObject<HTMLDivElement>, filename: string) => {
        if (ref.current) {
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                onclone: centerTitlesOnClone,
                ignoreElements: (el: Element) => el.classList.contains('export-ignore')
            } as any);
            const link = document.createElement('a');
            link.download = `${filename}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        }
    };

    const handleDownloadSVG = async (ref: React.RefObject<HTMLDivElement>, title: string | undefined, filename: string) => {
        if (ref.current) {
            const canvas = await html2canvas(ref.current, {
                scale: 2,
                useCORS: true,
                backgroundColor: '#ffffff',
                onclone: centerTitlesOnClone,
                ignoreElements: (el: Element) => el.classList.contains('export-menu') || el.classList.contains('export-dropdown') || el.classList.contains("export-ignore2")
            } as any);

            const width = canvas.width / 2;
            const height = canvas.height / 2;
            const dataURL = canvas.toDataURL('image/png');

            let svgTitleElement = '';
            let svgHeight = height;

            if (title) {
                const escapedTitle = title
                    .replace(/&/g, '&amp;')
                    .replace(/</g, '&lt;')
                    .replace(/>/g, '&gt;')
                    .replace(/"/g, '&quot;')
                    .replace(/'/g, '&apos;');

                svgTitleElement = `<text x="${width / 2}" y="25" text-anchor="middle" class="chart-title">${escapedTitle}</text>`;
                svgHeight = height + 40;
            }

            const svgContent = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" width="${width}" height="${svgHeight}" viewBox="0 0 ${width} ${svgHeight}">
  <style>
    .chart-title {
      font-family: 'Segoe UI', sans-serif;
      font-size: 16px;
      fill: #1a2a44;
      font-weight: bold;
    }
  </style>
  <rect width="100%" height="100%" fill="white"/>
  ${svgTitleElement}
  <image x="0" y="${title ? 40 : 0}" width="${width}" height="${height}" href="${dataURL}" preserveAspectRatio="xMidYMid meet"/>
</svg>`;

            const blob = new Blob([svgContent], { type: 'image/svg+xml;charset=utf-8' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `${filename}.svg`;
            link.click();
            URL.revokeObjectURL(url);
        }
    };

    return (
        <div id={generatePDFIds.dashboard()} className="prem-agreement-page">
            <div className="page-header">
                <h1 className="page-title">Patient Agreement to PREM Questions - Stratified</h1>
                <div className="controls">
                    <div className={`tmb-question-pill has-arrow ${questionDropdownOpen ? 'open' : ''}`} ref={questionDropdownRef} onClick={() => setQuestionDropdownOpen(!questionDropdownOpen)}>
                        <div className="select-display">
                            {selectedQuestionData?.code || 'Select question'}
                        </div>
                        <div className={`tmb-question-dropdown ${questionDropdownOpen ? 'show' : ''}`} onClick={(e) => e.stopPropagation()}>
                            <div className="tmb-question-dropdown-inner">
                                {questions.length > 0 ? questions.map(q => (
                                    <div
                                        key={q.name}
                                        className={`tmb-question-item ${q.name === selectedQuestion ? 'selected' : ''} ${isHighlightedQuestion(q) ? 'highlighted-question' : ''}`}
                                        onClick={() => {
                                            setSelectedQuestion(q.name);
                                            setQuestionDropdownOpen(false);
                                        }}
                                    >
                                        {q.code} - {q.fullText}
                                    </div>
                                )) : (
                                    <div className="tmb-question-item">No questions available</div>
                                )}
                            </div>
                        </div>
                    </div>
                    <CustomSelect
                        value={timePeriod}
                        onChange={setTimePeriod}
                        options={[
                            { value: 'monthly', label: 'Monthly' },
                            { value: 'quarterly', label: 'Quarterly' },
                            { value: 'bi-yearly', label: 'Bi-Yearly' },
                            { value: 'yearly', label: 'Yearly' },
                        ]}
                    />
                </div>
            </div>

            <div className="chart-section">
                <div id={generatePDFIds.chart(1)} className="chart-container" ref={premContainerRef}>
                    <div className="chart-header-row">
                        <div className="chart-title-block">
                            <h2 className="chart-title">Percentage of Patients who "StronglyAgree" & "Agree" by Race / Ethnicity</h2>
                            {selectedQuestionLabel && (
                                <p className="chart-subtitle">{selectedQuestionLabel}</p>
                            )}
                        </div>
                        <div className="chart-header-actions export-ignore export-ignore2">
                            <TmbPillSelect
                                displayText={
                                    selectedRaceGroups.length === 0
                                        ? 'Race/Ethnicity'
                                        : selectedRaceGroups.length === 1
                                            ? selectedRaceGroups[0]
                                            : `${selectedRaceGroups.length} selected`
                                }
                                variant="multiselect"
                                isOpen={raceDropdownOpen}
                                onToggle={setRaceDropdownOpen}
                            >
                                {(() => {
                                    const bipocItem = availableRaceGroups.find(r => isAllOthersValue(r));
                                    const nhWhiteItem = availableRaceGroups.find(r => isNHWhiteValue(r));
                                    const subItems = availableRaceGroups.filter(r => !isAllOthersValue(r) && !isNHWhiteValue(r));
                                    const allOthersActive = selectedRaceGroups.some(isAllOthersValue);
                                    const isMaxSelected = selectedRaceGroups.length >= BASE_MAX_RACE_SELECTIONS;

                                    const renderRaceItem = (race: string) => {
                                        const isChecked = selectedRaceGroups.includes(race);
                                        const isBIPOC = isAllOthersValue(race);
                                        const isNH = isNHWhiteValue(race);
                                        const isLockedChecked = allOthersActive && !isBIPOC && !isNH;
                                        const isDisabled = isLockedChecked || (!isChecked && isMaxSelected && !isBIPOC);
                                        return (
                                            <label
                                                key={race}
                                                className={`prem-race-item${isDisabled ? ' disabled' : ''}${isLockedChecked ? ' locked-checked' : ''}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    className="prem-race-checkbox"
                                                    checked={isLockedChecked ? true : isChecked}
                                                    onChange={(e) => handleRaceToggle(race, e.target.checked)}
                                                    disabled={isDisabled}
                                                />
                                                {race}
                                                {isBIPOC && (
                                                    <span className="prem-race-info" title="Select to view an aggregated comparison of all other race/ethnicity groups.">i</span>
                                                )}
                                            </label>
                                        );
                                    };
                                    return (
                                        <>
                                            {bipocItem && renderRaceItem(bipocItem)}
                                            {subItems.length > 0 && (
                                                <div className="prem-race-sub-options">
                                                    {subItems.map(race => renderRaceItem(race))}
                                                </div>
                                            )}
                                            {nhWhiteItem && renderRaceItem(nhWhiteItem)}
                                        </>
                                    );
                                })()}
                            </TmbPillSelect>
                            <div className="export-menu">
                                <button className="hamburger-btn" onClick={() => {
                                    const menu = document.getElementById('export-dropdown-menu-race');
                                    if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                                }}>&#9776;</button>
                                <div id="export-dropdown-menu-race" className="export-dropdown-menu">
                                    <div onClick={() => { handleDownloadSVG(premContainerRef, undefined, "PREM Agreement by Race Ethnicity"); closeMenu('export-dropdown-menu-race'); }}>Download SVG</div>
                                    <div onClick={() => { handleDownloadPNG(premContainerRef, "PREM Agreement by Race Ethnicity"); closeMenu('export-dropdown-menu-race'); }}>Download PNG</div>
                                    <div onClick={() => { handleDownloadRaceCSV(); closeMenu('export-dropdown-menu-race'); }}>Download CSV</div>
                                </div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={raceChartRef}
                        categories={raceChartData.categories}
                        series={raceChartData.series}
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={false}
                        yAxisLabel="% of Patients"
                        xAxisLabel="Time Period"
                        colors={raceChartColors}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="right"
                        showXAxisTooltip={true}
                        exportFilename="PREM Agreement by Race Ethnicity"
                        chartTitle={'Percentage of Patients who "StronglyAgree" & "Agree" by Race / Ethnicity'}
                        chartSubtitle={selectedQuestionLabel}
                        pageTitle="Patient Agreement to PREM Questions - Stratified"
                        isLoading={loading}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                        customTooltip={renderRaceTooltip}
                    />
                </div>

                <div id={generatePDFIds.chart(2)} className="chart-container" ref={premContainerRef2}>
                    <div className="chart-header-row">
                        <div className="chart-title-block">
                            <h2 className="chart-title">Percentage of Patients who "StronglyAgree" & "Agree" by Insurance Type</h2>
                            {selectedQuestionLabel && (
                                <p className="chart-subtitle">{selectedQuestionLabel}</p>
                            )}
                        </div>
                        <div className="export-menu export-ignore">
                            <button className="hamburger-btn" onClick={() => {
                                const menu = document.getElementById('export-dropdown-menu-insurance');
                                if (menu) menu.style.display = menu.style.display === 'block' ? 'none' : 'block';
                            }}>&#9776;</button>
                            <div id="export-dropdown-menu-insurance" className="export-dropdown-menu">
                                <div onClick={() => { handleDownloadSVG( premContainerRef2,undefined ,"PREM Agreement by Insurance Type");; closeMenu('export-dropdown-menu-insurance'); }}>Download SVG</div>
                                <div onClick={() => { handleDownloadPNG(premContainerRef2,"PREM Agreement by Insurance Type"); closeMenu('export-dropdown-menu-insurance'); }}>Download PNG</div>
                                <div onClick={() => { handleDownloadInsuranceCSV(); closeMenu('export-dropdown-menu-insurance'); }}>Download CSV</div>
                            </div>
                        </div>
                    </div>
                    <LineChart
                        ref={insuranceChartRef}
                        categories={insuranceChartData.categories}
                        series={insuranceChartData.series}
                        min={0}
                        max={100}
                        tickAmount={20}
                        markers={false}
                        yAxisLabel="% of Patients"
                        xAxisLabel="Time Period"
                        colors={INSURANCE_CHART_COLORS}
                        fontFamily="Helvetica, Arial, sans-serif"
                        legendPosition="top"
                        legendAlign="right"
                        showXAxisTooltip={true}
                        exportFilename="PREM Agreement by Insurance Type"
                        chartTitle={'Percentage of Patients who "StronglyAgree" & "Agree" by Insurance Type'}
                        chartSubtitle={selectedQuestionLabel}
                        pageTitle="Patient Agreement to PREM Questions - Stratified"
                        isLoading={insuranceLoading}
                        yAxisDecimals={1}
                        decimals={2}
                        xAxisLabelFontSize="14px"
                        customTooltip={renderInsuranceTooltip}
                    />
                </div>
            </div>
        </div>
    );
};

export default PremAgreement;
